import React from 'react';
import { useSelector } from 'react-redux';
import {
  Ad,
  AppContent,
  ArticleContent,
  ArticleImage,
  ArticleMainImage,
  ArticleTitle,
  ContentAndSidebar,
  DecoratedQuote,
  Divider,
  ErrorBoundary,
  PostMainContent,
  RecommendationsAdvertisement,
  ShareButton,
  Sponsor,
  Slideshow,
} from 'mm-ui-components';
import { Store } from 'redux';
import { Theme } from '../../components/connectedComponents/Theme';
import { PostMetaTags } from '../../components/connectedComponents/PostMetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { Page } from '../Page';
import {
  getAuthors,
  getBody,
  getBy,
  getCover,
  getShareConfig,
  getSlideshowData,
  getSponsor,
  getTitle,
  getUpdatedAtDateFormatted,
  getUpdatedAtTimeAndDateFormatted,
} from '../../store/template/postPage/postPage.selectors';
import { getAdSlotId, getCanonicalUrl, getSiteName, getTemplateName } from '../../store/config/config.selectors';
import { SLOT_NAMES } from '../templates.const';
import { PostSidebarConnected } from '../../components/connectedComponents/PostSidebarConnected';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import {
  getRevcontentWidgetID,
  getShowRecommendationsAdvertisement,
} from '../../store/template/postPageWithRecommendationsAdvertisement/postPageWithRecommendationsAdvertisement.selectors';
import { createDefaultBlocksWidths } from '../postPage.util';
import { DefaultNavigationLayout } from '../../components/connectedComponents/DefaultNavigationLayout';

interface PostPageProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

const blocksWithMultipleOptions = {
  divider: Divider,
  quote: DecoratedQuote,
  image: ArticleImage,
};

export const PostPageBaliComponent: React.FunctionComponent<PostPageProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <PostPageBaliContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

export const PostPageBaliContentConnected: React.FunctionComponent = () => {
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
  const sideBar1Id = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_1));
  const sideBar2Id = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_2));
  const title = useSelector(getTitle);
  const by = useSelector(getBy);
  const authors = useSelector(getAuthors);
  const updatedAtDateFormatted = useSelector(getUpdatedAtDateFormatted);
  const updatedAtTimeAndDateFormatted = useSelector(getUpdatedAtTimeAndDateFormatted);
  const cover = useSelector(getCover);
  const body = useSelector(getBody);
  const shareConfig = useSelector(getShareConfig);
  const canonical = useSelector(getCanonicalUrl);
  const templateName = useSelector(getTemplateName);
  const siteName = useSelector(getSiteName);
  const sponsor = useSelector(getSponsor);
  const showRecommendationsAdvertisement = useSelector(getShowRecommendationsAdvertisement);
  const slideshow = useSelector(getSlideshowData);
  const revcontentWidgetID = useSelector(getRevcontentWidgetID);
  const defaultBlocksWidths = createDefaultBlocksWidths();

  return (
    <React.Fragment>
      <PostMetaTags />
      <HeadScripts />
      <Theme>
        <AppContent>
          <DefaultNavigationLayout mainWrapperConfigThemeKey="mainArticleContent">
            <Ad id={topAdId} />
            <ContentAndSidebar>
              <PostMainContent>
                <header>
                  <ArticleTitle
                    title={title}
                    by={by}
                    updatedAtDateFormatted={updatedAtDateFormatted}
                    updatedAtTimeAndDateFormatted={updatedAtTimeAndDateFormatted}
                    authors={authors}
                  >
                    <ShareButton shareConfig={shareConfig} canonical={canonical} />
                  </ArticleTitle>
                  <ArticleMainImage
                    {...cover}
                    siteName={siteName}
                    templateName={templateName}
                    defaultBlocksWidths={defaultBlocksWidths}
                  />
                </header>
                {sponsor ? <Sponsor {...sponsor} /> : null}
                <Slideshow {...slideshow} blockWidths={defaultBlocksWidths}>
                  <ArticleContent
                    blocksWithMultipleOptions={blocksWithMultipleOptions}
                    body={body}
                    templateName={templateName}
                    siteName={siteName}
                    defaultBlocksWidths={defaultBlocksWidths}
                  />
                </Slideshow>
              </PostMainContent>
              <PostSidebarConnected adId1={sideBar1Id} adId2={sideBar2Id} />
              {showRecommendationsAdvertisement
                ? <RecommendationsAdvertisement revcontentWidgetID={revcontentWidgetID} /> : null}
            </ContentAndSidebar>
            <Ad id={oneOnOneId} />
          </DefaultNavigationLayout>
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};
